import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import Select from 'react-select'
import { Modal } from 'react-bootstrap'
import QRCode from "qrcode.react"

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { copyText, notify } from 'helpers/helpers'
import { createZip, dataUrlToBytes } from 'helpers/zip'
import ChannelPosesMenu, { saveChannelPosesMenu } from 'pages/parts/ChannelPosesMenu'
import PageTabs from 'pages/parts/PageTabs'

// Имя файла из зала и стола: без символов, запрещённых в Windows
function qrFileName(table) {
	const clean = (value) => String(value ?? '').replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim()
	return [clean(table.hall_name), clean(table.name)].filter(Boolean).join(' - ') || `table-${table.id}`
}

function normalizePoseStatus(value) {
	return value === true || value === 1 || value === '1' || value === 't' || value === 'true'
}

function mergePoses(savedPoses = [], helperPoses = []) {
	return helperPoses.map((helperPos) => {
		const saved = savedPoses.find((item) => Number(item.id) === Number(helperPos.id))

		if (!saved) {
			return {
				...helperPos,
				status: false,
			}
		}

		return {
			...helperPos,
			...saved,
			status: normalizePoseStatus(saved.status),
		}
	})
}

const DEFAULT_ACTIONS = [
	{ code: 'call_waiter', label: '', is_enabled: false },
	{ code: 'request_bill', label: '', is_enabled: false },
]

const DEFAULT_ACTION_LABELS = {
	call_waiter: 'Вызов официанта',
	request_bill: 'Счёт',
}

function normalizeActionLabel(code, label) {
	const trimmed = String(label ?? '').trim()
	if (!trimmed) return ''
	if (DEFAULT_ACTION_LABELS[code] && trimmed === DEFAULT_ACTION_LABELS[code]) return ''
	return trimmed
}

function Index() {
	const { t } = useTranslation()

	const [data, setData] = useState({
		name: 'QR меню стола',
		cooldown_seconds: 60,
		external_menu_id: null,
		default_language_id: null,
		languages: [],
		actions: DEFAULT_ACTIONS,
		poses: [],
	})
	const [activeTab, setActiveTab] = useState('general')
	const [languages, setLanguages] = useState([])
	const [externalMenus, setExternalMenus] = useState([])
	const [priceCategories, setPriceCategories] = useState([])
	const [posMenus, setPosMenus] = useState({})
	const [selectedPosId, setSelectedPosId] = useState(null)
	const [selectedPosUuid, setSelectedPosUuid] = useState('')
	const [tables, setTables] = useState([])
	const [selectedTable, setSelectedTable] = useState(null)
	const [showQrModal, setShowQrModal] = useState(false)
	const [bulkDownload, setBulkDownload] = useState(false)
	// Точки бренда из /helper/poses — для повторной загрузки настройки без справочников
	const helperPoses = useRef([])

	// Справочники (точки, языки, меню, категории цен) грузятся один раз при открытии:
	// после сохранения перечитываем только саму настройку, а не всю страницу
	async function searchWithFilter(withReferences = true) {
		const [settingsResponse, posesResponse, languagesResponse, externalMenusResponse, priceCategoriesResponse] = await Promise.all([
			GET('/chain/qr-menu-table', {}, { loader: true }),
			withReferences ? GET('/helper/poses') : { status: 200, data: helperPoses.current },
			withReferences ? GET('/helper/languages') : null,
			withReferences ? GET('/helper/external-menu') : null,
			withReferences ? GET('/price-categories') : null,
		])

		if (!httpOk(settingsResponse)) return

		if (withReferences) {
			if (httpOk(posesResponse)) helperPoses.current = Array.isArray(posesResponse.data) ? posesResponse.data : []
			if (httpOk(languagesResponse)) setLanguages(languagesResponse.data ?? [])
			if (httpOk(externalMenusResponse)) setExternalMenus(externalMenusResponse.data ?? [])
			if (httpOk(priceCategoriesResponse)) setPriceCategories(priceCategoriesResponse.data?.data ?? [])
		}

		const settingsData = {
			name: 'QR меню стола',
			cooldown_seconds: 60,
			external_menu_id: null,
			default_language_id: null,
			languages: [],
			actions: DEFAULT_ACTIONS.map((item) => ({ ...item })),
			poses: [],
			...(settingsResponse.data ?? {}),
		}

		if (!Array.isArray(settingsData.actions) || settingsData.actions.length === 0) {
			settingsData.actions = DEFAULT_ACTIONS.map((item) => ({ ...item }))
		} else {
			settingsData.actions = settingsData.actions.map((item) => ({
				code: item.code,
				label: normalizeActionLabel(item.code, item.label),
				is_enabled: normalizePoseStatus(item.is_enabled),
			}))
		}

		if (!Array.isArray(settingsData.languages)) {
			settingsData.languages = []
		}

		if (httpOk(posesResponse)) {
			const savedPoses = Array.isArray(settingsData.poses) ? settingsData.poses : []
			const helperList = Array.isArray(posesResponse.data) ? posesResponse.data : []
			settingsData.poses = mergePoses(savedPoses, helperList)
		}

		setData(settingsData)

		// Выбранную точку не сбрасываем: иначе после сохранения заново грузятся её столы
		setSelectedPosId((current) => {
			if (current && (settingsData.poses ?? []).some((item) => item.id === current)) return current
			const firstEnabled = (settingsData.poses ?? []).find((item) => item.status)
			const firstPose = (settingsData.poses ?? [])[0]
			return firstEnabled?.id ?? firstPose?.id ?? null
		})
	}

	async function createUpdate(e) {
		e.preventDefault()

		const payload = {
			...data,
			cooldown_seconds: Number(data.cooldown_seconds) || 0,
			external_menu_id: data.external_menu_id || null,
			default_language_id: data.default_language_id || null,
			languages: (data.languages ?? []).map((item) => ({
				id: item.id,
			})),
			actions: (data.actions ?? []).map((item) => ({
				code: item.code,
				label: String(item.label ?? '').trim(),
				is_enabled: Boolean(item.is_enabled),
			})),
			poses: (data.poses ?? []).map((item) => ({
				id: item.id,
				status: Boolean(item.status),
			})),
		}

		// Тост «Успешно» один — после того как сохранилось и меню филиалов, поэтому у самого запроса он выключен
		let response
		if (data.id) {
			response = await PUT('/chain/qr-menu-table', payload, { loader: true, notification: false })
		} else {
			response = await POST('/chain/qr-menu-table', payload, { loader: true, notification: false })
		}

		if (httpOk(response)) {
			await saveChannelPosesMenu(posMenus)
			notify(t('success'), 'success')
			searchWithFilter(false)
		}
	}

	function handlePoseStatus(index, checked) {
		const poses = [...(data.poses ?? [])]
		poses[index] = {
			...poses[index],
			status: checked,
		}
		setData({ ...data, poses })
	}

	function handleActionChange(index, field, value) {
		const actions = [...(data.actions ?? [])]
		actions[index] = {
			...actions[index],
			[field]: value,
		}
		setData({ ...data, actions })
	}

	async function loadTables(posId = selectedPosId) {
		if (!posId) {
			setTables([])
			setSelectedPosUuid('')
			return
		}

		const response = await GET(`/chain/qr-menu-table/tables?pos_id=${posId}`, {}, { loader: true })
		if (httpOk(response)) {
			setTables(Array.isArray(response.data?.tables) ? response.data.tables : [])
			setSelectedPosUuid(response.data?.pos?.uuid || '')
		}
	}

	function getBrandQrUrl() {
		if (!selectedPosUuid) return ''
		return `https://service.mison.uz/menu/${selectedPosUuid}`
	}

	function getTableQrUrl(table) {
		if (!selectedPosUuid || !table?.uuid) return ''
		return `https://service.mison.uz/menu/${selectedPosUuid}/table/${table.uuid}`
	}

	function openTableQr(table) {
		setSelectedTable(table)
		setShowQrModal(true)
	}

	function downloadBrandQRCode() {
		const canvas = document.getElementById("qr-brand-gen");
		if (!canvas) return

		const pngUrl = canvas
			.toDataURL("image/png")
			.replace("image/png", "image/octet-stream");
		const downloadLink = document.createElement("a");
		downloadLink.href = pngUrl;
		downloadLink.download = 'qr-menu.png';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		document.body.removeChild(downloadLink);
	}

	function downloadQRCode(table = selectedTable) {
		const canvas = document.getElementById("qr-table-gen");
		if (!canvas) return

		const pngUrl = canvas
			.toDataURL("image/png")
			.replace("image/png", "image/octet-stream");
		const downloadLink = document.createElement("a");
		downloadLink.href = pngUrl;
		downloadLink.download = `qr-table-${table?.name || 'table'}.png`;
		document.body.appendChild(downloadLink);
		downloadLink.click();
		document.body.removeChild(downloadLink);
	}

	function printTableQr() {
		const url = getTableQrUrl(selectedTable)
		if (!url) return

		const canvas = document.getElementById("qr-table-gen");
		if (!canvas) return

		const dataUrl = canvas.toDataURL("image/png")
		const win = window.open('', '_blank')
		if (!win) return

		win.document.write(`
			<html>
				<head><title>${selectedTable?.name || 'QR'}</title></head>
				<body style="text-align:center;font-family:sans-serif;">
					<h2>${selectedTable?.hall_name || ''} / ${selectedTable?.name || ''}</h2>
					<img src="${dataUrl}" />
					<p>${url}</p>
					<script>window.onload = function(){ window.print(); }</script>
				</body>
			</html>
		`)
		win.document.close()
	}

	// Все QR одним архивом: канвасы рисуются в скрытом блоке, после отрисовки собираем PNG в zip
	function downloadAllQRCodes() {
		if (!selectedPosUuid || tables.length === 0) return
		setBulkDownload(true)
	}

	useEffect(() => {
		if (!bulkDownload) return
		const frame = requestAnimationFrame(() => {
			const usedNames = {}
			const files = []
			tables.forEach((table) => {
				const canvas = document.getElementById(`qr-bulk-${table.id}`)
				if (!canvas) return
				const base = qrFileName(table)
				usedNames[base] = (usedNames[base] || 0) + 1
				const name = usedNames[base] > 1 ? `${base} (${usedNames[base]})` : base
				files.push({ name: `${name}.png`, data: dataUrlToBytes(canvas.toDataURL('image/png')) })
			})

			if (files.length > 0) {
				const posName = (data.poses ?? []).find((item) => Number(item.id) === Number(selectedPosId))?.name
				const link = document.createElement('a')
				link.href = URL.createObjectURL(createZip(files))
				link.download = `qr-${qrFileName({ name: posName || 'tables' })}.zip`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				setTimeout(() => URL.revokeObjectURL(link.href), 1000)
			}
			setBulkDownload(false)
		})
		return () => cancelAnimationFrame(frame)
	}, [bulkDownload]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		loadTables(selectedPosId)
	}, [selectedPosId]) // eslint-disable-line react-hooks/exhaustive-deps

	const menuOptions = [
		{ id: null, name: t('main_menu') },
		...externalMenus,
	]
	const selectedMenuOption = data.external_menu_id
		? menuOptions.find((option) => Number(option.id) === Number(data.external_menu_id))
		: menuOptions[0]

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('qr_menu_table')}
					</h4>
				</div>
			</div>

			<form className="card page-compact" onSubmit={createUpdate}>
				<div className="card-body">
					<PageTabs
						active={activeTab}
						onChange={setActiveTab}
						tabs={[
							{ key: 'general', label: t('general') },
							{ key: 'poses', label: t('poses') },
							{ key: 'qr_codes', label: t('qr_codes') },
						]} />

					<div className={activeTab !== 'general' ? 'd-none' : 'row'}>
						<div className="col-lg-6">
							<div className="form-group">
								<label>{t('name2')}<span className="required-mark">*</span></label>
								<input
									type="text"
									className="form-control"
									name="name"
									autoComplete="off"
									value={data.name ?? ''}
									onChange={(e) => setData({ ...data, name: e.target.value })}
								/>
							</div>

							<div className="form-group">
								<label>{t('site_menu')}<span className="required-mark">*</span></label>
								<Select
									options={menuOptions}
									value={selectedMenuOption || null}
									onChange={(option) => setData({ ...data, external_menu_id: option?.id ?? null })}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id ?? 'main'}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
								/>
								<Select
									isClearable
									className="mt-2"
									options={priceCategories}
									value={priceCategories.find((option) => option.id === data.price_category_id) || ''}
									onChange={(option) => setData({ ...data, price_category_id: option ? option.id : null })}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
									placeholder={t('base_price')}
									noOptionsMessage={() => t('list_empty')}
								/>
							</div>

							<div className="form-group">
								<label>{t('default_language')}<span className="required-mark">*</span></label>
								<Select
									options={languages}
									value={languages.find((option) => option.id === data.default_language_id) || null}
									onChange={(option) => setData({ ...data, default_language_id: option ? option.id : null })}
									getOptionLabel={(option) => `${option.native_name ?? option.name} (${option.code})`}
									getOptionValue={(option) => option.id}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
								/>
							</div>

							<div className="form-group">
								<label>{t('site_languages')}<span className="required-mark">*</span></label>
								<Select
									isMulti
									options={languages}
									value={data.languages ?? []}
									onChange={(options) => setData({ ...data, languages: options ?? [] })}
									getOptionLabel={(option) => `${option.native_name ?? option.name} (${option.code})`}
									getOptionValue={(option) => option.id}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
								/>
							</div>
						</div>

						<div className="col-lg-6">
							<div className="form-group">
								<label>{t('cooldown_seconds')}</label>
								<input
									type="number"
									min="0"
									max="3600"
									className="form-control"
									value={data.cooldown_seconds ?? 60}
									onChange={(e) => setData({ ...data, cooldown_seconds: e.target.value })}
								/>
							</div>

							<div className="form-group">
								<div className="form-check form-switch form-switch-sm mb-1">
									<input
										id="qr_cart_enabled"
										type="checkbox"
										className="form-check-input"
										checked={data.is_cart_enabled ? true : false}
										onChange={(e) => setData({ ...data, is_cart_enabled: e.target.checked })}
									/>
									<label className="form-check-label" htmlFor="qr_cart_enabled">{t('qr_cart_enabled')}</label>
								</div>
								<small className="text-muted">{t('qr_cart_enabled_hint')}</small>
							</div>

							<div className="form-group">
								<div className="form-check form-switch form-switch-sm mb-1">
									<input
										id="qr_feedback_enabled"
										type="checkbox"
										className="form-check-input"
										checked={data.is_feedback_enabled ? true : false}
										onChange={(e) => setData({ ...data, is_feedback_enabled: e.target.checked })}
									/>
									<label className="form-check-label" htmlFor="qr_feedback_enabled">{t('qr_feedback_enabled')}</label>
								</div>
								<small className="text-muted">{t('qr_feedback_enabled_hint')}</small>
							</div>

							<label className="mb-2">{t('actions')}</label>
							{(data.actions ?? []).map((action, index) => (
								<div key={action.code} className="d-flex align-items-center gap-2 mb-2">
									<input
										type="text"
										className="form-control"
										value={action.label ?? ''}
										placeholder={t(`qr_action_${action.code}`)}
										onChange={(e) => handleActionChange(index, 'label', e.target.value)}
									/>
									<div className="form-check form-switch form-switch-sm mb-0">
										<input
											type="checkbox"
											className="form-check-input"
											checked={action.is_enabled ? true : false}
											onChange={(e) => handleActionChange(index, 'is_enabled', e.target.checked)}
										/>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className={activeTab !== 'poses' ? 'd-none' : ''}>
						<ChannelPosesMenu
							poses={data.poses ?? []}
							overrides={posMenus}
							setOverrides={setPosMenus}
							onToggle={(e, index) => handlePoseStatus(index, e.target.checked)} />
					</div>

					<div className={activeTab !== 'qr_codes' ? 'd-none' : 'row'}>
						<div className="col-lg-5">
							<div className="border rounded p-3 mb-3">
								{selectedPosUuid ? (
									<>
										<label>URL</label>
										<div className="d-flex gap-2 mb-3">
											<input
												type="text"
												readOnly
												className="form-control"
												value={getBrandQrUrl()}
											/>
											<button
												type="button"
												className="btn btn-outline-primary text-nowrap"
												onClick={() => {
													copyText(getBrandQrUrl())
													notify(t('copied'), 'success')
												}}
											>
												{t('copy')}
											</button>
										</div>
										<div className="d-flex justify-content-center">
											<QRCode
												id="qr-brand-gen"
												value={getBrandQrUrl()}
												size={220}
												level={"H"}
												includeMargin={true}
											/>
										</div>
										<div className="d-flex justify-content-center mt-3">
											<button
												type="button"
												className="btn btn-primary"
												onClick={downloadBrandQRCode}
											>
												{t('download')}
											</button>
										</div>
									</>
								) : (
									<div className="text-muted">{t('list_empty')}</div>
								)}
							</div>
						</div>

						<div className="col-lg-7">
							<div className="border rounded p-3 mb-3">
								<div className="form-group">
									<label>{t('pos')}</label>
									<Select
										options={data.poses ?? []}
										value={(data.poses ?? []).find((option) => Number(option.id) === Number(selectedPosId)) || ''}
										onChange={(option) => setSelectedPosId(option ? option.id : null)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="d-flex justify-content-end mb-2">
									<button
										type="button"
										className="btn btn-sm btn-outline-primary"
										disabled={!selectedPosUuid || tables.filter((item) => item.uuid).length === 0 || bulkDownload}
										onClick={downloadAllQRCodes}
									>
										<i className="uil uil-import me-1"></i>
										{t('download_all_qr')}
									</button>
								</div>
								<div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
									<table className="table table-bordered mb-0">
										<thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--dark-surface, #fff)' }}>
											<tr>
												<th style={{ width: '33px', minWidth: '33px' }} className="text-center">#</th>
												<th>{t('hall')}</th>
												<th>{t('table')}</th>
												<th style={{ width: '33px', minWidth: '33px', maxWidth: '33px' }}></th>
											</tr>
										</thead>
										<tbody>
											{tables.map((item, index) => (
												<tr key={item.id}>
													<td style={{ width: '33px' }} className="text-center">{index + 1}</td>
													<td>{item.hall_name}</td>
													<td>{item.name}</td>
													<td style={{ width: '33px', minWidth: '33px', maxWidth: '33px', padding: '4px' }} className="text-center">
														<button
															type="button"
															className="btn btn-sm btn-outline-primary p-0"
															style={{ width: '28px', height: '28px' }}
															disabled={!item.uuid || !selectedPosUuid}
															onClick={() => openTableQr(item)}
															title="QR"
														>
															<i className="uil uil-qrcode-scan" style={{ fontSize: '14px' }}></i>
														</button>
													</td>
												</tr>
											))}
											{tables.length === 0 && (
												<tr>
													<td colSpan={4} className="text-center text-muted">
														{t('list_empty')}
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>

					{/* QR-коды ничего не сохраняют — кнопки формы там только путали бы */}
					{activeTab !== 'qr_codes' &&
						<div className="btn-wrapper gap-2">
							<Link className="btn btn-warning btn-rounded btn-wide" to="/external-integrations">
								{t('cancel')}
							</Link>
							<button type="submit" className="btn btn-primary btn-rounded btn-wide">
								{t('save')}
							</button>
						</div>
					}
				</div>
			</form>

			{bulkDownload && (
				<div style={{ position: 'fixed', left: '-10000px', top: 0 }} aria-hidden="true">
					{tables.filter((item) => item.uuid).map((item) => (
						<QRCode
							key={item.id}
							id={`qr-bulk-${item.id}`}
							value={getTableQrUrl(item)}
							size={280}
							level={"H"}
							includeMargin={true}
						/>
					))}
				</div>
			)}

			<Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedTable?.hall_name} / {selectedTable?.name}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedTable && (
						<>
							<label>URL</label>
							<div className="d-flex gap-2 mb-3">
								<input
									type="text"
									readOnly
									className="form-control"
									value={getTableQrUrl(selectedTable)}
								/>
								<button
									type="button"
									className="btn btn-outline-primary text-nowrap"
									onClick={() => {
										copyText(getTableQrUrl(selectedTable))
										notify(t('copied'), 'success')
									}}
								>
									{t('copy')}
								</button>
							</div>
							<div className="d-flex justify-content-center">
								<QRCode
									id="qr-table-gen"
									value={getTableQrUrl(selectedTable)}
									size={280}
									level={"H"}
									includeMargin={true}
								/>
							</div>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-secondary" onClick={printTableQr}>
						{t('print')}
					</button>
					<button type="button" className="btn btn-primary" onClick={() => downloadQRCode()}>
						{t('download')}
					</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default Index
